import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Link2, Gauge, Fingerprint, History, Loader2, Search, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

// Reusable dialog to link an account (Common Area / Unit) to meters + one identifier,
// with a change-confirmation prompt and an audit trail. Works across
// utility / vendor / unit account collections (backend resolves the row).
export const AccountLinkDialog = ({ open, onOpenChange, orgAccountId, account, identifiers = [], onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedMeterIds, setSelectedMeterIds] = useState([]);
  const [identifierId, setIdentifierId] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [meterSearch, setMeterSearch] = useState('');
  const [meterOptions, setMeterOptions] = useState([]);   // server-searched meters
  const [linkedMeterDocs, setLinkedMeterDocs] = useState([]); // currently linked (always shown)
  const [searchingMeters, setSearchingMeters] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [identOpen, setIdentOpen] = useState(false);
  const [identSearch, setIdentSearch] = useState('');
  const [pendingChanges, setPendingChanges] = useState(null);

  // Fetch current links when opened
  useEffect(() => {
    if (!open || !account?.id || !orgAccountId) return;
    setLoading(true);
    setMeterSearch('');
    setShowAudit(false);
    setPendingChanges(null);
    setIdentSearch('');
    api.get(`/ubm/accounts/${orgAccountId}/account-links/${account.id}`)
      .then(({ data }) => {
        setSelectedMeterIds(data.linked_meter_ids || []);
        setIdentifierId(data.linked_identifier_id || '');
        setAuditLog(data.audit_log || []);
        setLinkedMeterDocs(data.meters || []);
        setMeterOptions(data.meters || []);
      })
      .catch(() => {
        setSelectedMeterIds([]); setIdentifierId(''); setAuditLog([]);
        setLinkedMeterDocs([]); setMeterOptions([]);
      })
      .finally(() => setLoading(false));
  }, [open, account?.id, orgAccountId]);

  // Server-side meter search (debounced) — handles orgs with thousands of meters.
  const runMeterSearch = useCallback(async (q) => {
    if (!orgAccountId) return;
    setSearchingMeters(true);
    try {
      const { data } = await api.get(`/ubm/accounts/${orgAccountId}/meters`, {
        params: { search: q || undefined, page_size: 100 } });
      setMeterOptions(data?.items || data || []);
    } catch {
      setMeterOptions([]);
    } finally {
      setSearchingMeters(false);
    }
  }, [orgAccountId]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      runMeterSearch(meterSearch.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [meterSearch, open, runMeterSearch]);

  // Merge linked meters (always available to toggle) with the search results,
  // commodity-matching first, deduped by id.
  const displayMeters = useMemo(() => {
    const c = (account?.commodity || '').toLowerCase();
    const map = new Map();
    [...linkedMeterDocs, ...meterOptions].forEach(m => { if (m?.id) map.set(m.id, m); });
    return [...map.values()].sort((a, b) => {
      const am = (a.commodity || '').toLowerCase() === c ? 0 : 1;
      const bm = (b.commodity || '').toLowerCase() === c ? 0 : 1;
      return am - bm;
    });
  }, [linkedMeterDocs, meterOptions, account?.commodity]);

  const identLabel = useMemo(() => {
    const e = identifiers.find(x => x.id === identifierId);
    if (!e) return '';
    return `${e.identifier_number}${e.utility_type ? ` · ${e.utility_type}` : ''}`;
  }, [identifierId, identifiers]);

  const filteredIdentifiers = useMemo(() => {
    const q = identSearch.trim().toLowerCase();
    const list = q
      ? identifiers.filter(e => `${e.identifier_number} ${e.utility_type || ''}`.toLowerCase().includes(q))
      : identifiers;
    return list.slice(0, 100);
  }, [identifiers, identSearch]);

  const toggleMeter = (id) => {
    setSelectedMeterIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const doSave = async (confirmed) => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/ubm/accounts/${orgAccountId}/account-links/${account.id}`,
        { linked_meter_ids: selectedMeterIds, linked_identifier_id: identifierId || null, confirmed });
      if (data.requires_confirmation) {
        setPendingChanges(data.changes);
        setSaving(false);
        return;
      }
      setAuditLog(data.audit_log || []);
      setLinkedMeterDocs(data.meters || []);
      toast.success('Links updated');
      setPendingChanges(null);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update links');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-[520px] w-[520px] flex flex-col p-0" data-testid="account-link-drawer">
          <SheetHeader className="px-6 pt-6 pb-2">
            <SheetTitle className="flex items-center gap-2"><Link2 className="h-4 w-4" /> Link Meters & Identifier</SheetTitle>
            <SheetDescription>
              Account <span className="font-mono">{account?.account_number || '—'}</span>
              {account?.commodity && <> · <span className="capitalize">{account.commodity}</span></>}
              . One identifier per account; many meters per commodity.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading links…
              </div>
            ) : (
              <>
                {/* Identifier */}
                <div>
                  <Label className="flex items-center gap-1 mb-1"><Fingerprint className="h-3.5 w-3.5" /> Identifier</Label>
                  <Popover open={identOpen} onOpenChange={setIdentOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between rounded-none font-normal" data-testid="link-identifier-select">
                        <span className={identLabel ? '' : 'text-muted-foreground'}>{identLabel || 'Select identifier'}</span>
                        <ChevronsUpDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[440px] p-0 rounded-none" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput placeholder="Search identifier…" value={identSearch} onValueChange={setIdentSearch} data-testid="link-identifier-search" />
                        <CommandList>
                          <CommandEmpty>No identifiers found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem value="__none__" onSelect={() => { setIdentifierId(''); setIdentOpen(false); }} data-testid="link-identifier-none">
                              <Check className={`mr-2 h-4 w-4 ${identifierId ? 'opacity-0' : 'opacity-100'}`} />
                              -- Not Linked --
                            </CommandItem>
                            {filteredIdentifiers.map(e => (
                              <CommandItem key={e.id} value={e.id} onSelect={() => { setIdentifierId(e.id); setIdentOpen(false); }} data-testid={`link-identifier-${e.id}`}>
                                <Check className={`mr-2 h-4 w-4 ${identifierId === e.id ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="font-mono">{e.identifier_number}</span>
                                {e.utility_type && <span className="text-muted-foreground ml-2 text-xs">· {e.utility_type}</span>}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground mt-1">NMI / MPAN / MPRN / ICP / account identifier.</p>
                </div>

                {/* Meters */}
                <div>
                  <Label className="flex items-center gap-1 mb-1"><Gauge className="h-3.5 w-3.5" /> Meters ({selectedMeterIds.length} selected)</Label>
                  <div className="relative mb-2">
                    <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input className="rounded-none pl-7 h-8 text-sm" placeholder="Search meter number…" value={meterSearch} onChange={(e) => setMeterSearch(e.target.value)} data-testid="link-meter-search" />
                  </div>
                  <div className="border rounded-none max-h-64 overflow-y-auto divide-y" data-testid="link-meter-list">
                    {searchingMeters ? (
                      <div className="p-4 text-center text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Searching…</div>
                    ) : displayMeters.length === 0 ? (
                      <div className="p-4 text-center text-xs text-muted-foreground">No meters found.</div>
                    ) : displayMeters.map(m => (
                      <label key={m.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-muted/40" data-testid={`link-meter-${m.id}`}>
                        <Checkbox checked={selectedMeterIds.includes(m.id)} onCheckedChange={() => toggleMeter(m.id)} />
                        <span className="font-mono">{m.meter_number}</span>
                        {m.commodity && <Badge variant="outline" className="rounded-none text-[10px] capitalize ml-auto">{m.commodity}</Badge>}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Type to search across all meters. Selected meters stay checked even while searching.</p>
                </div>

                {/* Audit */}
                <div>
                  <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setShowAudit(s => !s)} data-testid="link-audit-toggle">
                    <History className="h-3.5 w-3.5" /> {showAudit ? 'Hide' : 'Show'} change history ({auditLog.length})
                  </button>
                  {showAudit && (
                    <div className="mt-2 border rounded-none divide-y text-xs max-h-48 overflow-y-auto" data-testid="link-audit-log">
                      {auditLog.length === 0 ? (
                        <div className="p-3 text-muted-foreground">No changes recorded yet.</div>
                      ) : [...auditLog].reverse().map((a, i) => (
                        <div key={i} className="p-2">
                          <div className="font-medium">{a.action === 'meters_changed' ? 'Meters changed' : a.action === 'identifier_changed' ? 'Identifier changed' : a.action}</div>
                          <div className="text-muted-foreground">
                            {Array.isArray(a.from) ? (a.from.join(', ') || '—') : (a.from || '—')} → {Array.isArray(a.to) ? (a.to.join(', ') || '—') : (a.to || '—')}
                          </div>
                          <div className="text-[10px] text-muted-foreground">{a.by} · {a.ts ? new Date(a.ts).toLocaleString() : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 px-6 py-4 border-t bg-background">
            <Button type="button" variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="button" className="rounded-none" disabled={saving || loading} onClick={() => doSave(false)} data-testid="save-account-links-btn">
              {saving ? 'Saving…' : 'Save Links'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Change confirmation */}
      <AlertDialog open={!!pendingChanges} onOpenChange={(o) => { if (!o) setPendingChanges(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="link-change-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm link changes</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>You are changing the meters / identifier attached to this account. Please confirm:</p>
                {pendingChanges?.identifier?.changed && (
                  <div><span className="font-medium">Identifier:</span> {pendingChanges.identifier.from || '—'} → {pendingChanges.identifier.to || '—'}</div>
                )}
                {(pendingChanges?.meters?.removed?.length > 0) && (
                  <div><span className="font-medium">Removing meters:</span> {pendingChanges.meters.removed.join(', ')}</div>
                )}
                {(pendingChanges?.meters?.added?.length > 0) && (
                  <div><span className="font-medium">Adding meters:</span> {pendingChanges.meters.added.join(', ')}</div>
                )}
                <p className="text-xs text-muted-foreground">This change will be recorded with your name and the time.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none" onClick={() => doSave(true)} data-testid="link-change-confirm-yes">Yes, change it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AccountLinkDialog;
